import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import {
  SaveAnalysisUserDatasetError,
  useSaveAnalysisUserDatasetMutation,
  useSaveAnalysisUserDatasetV2Mutation
} from "@/api/analysis/useAnalysisAPI";
import DatasetList from "@/components/dataset/datasetList";
import NotificationContent from "@/components/notification/notificationContent";
import { Button } from "@/components/ui/button";
import ButtonMenu, { ButtonMenuItem } from "@/components/ui/buttonMenu";
import { MESSAGE } from "@/constants/message";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useConfiguration } from "@/hooks/project/useConfiguration";
import { useAnalysisConfigHydration } from "@/hooks/spatialAnalysis/useAnalysisConfigHydration";
import { useAnalysisResultSummary } from "@/hooks/spatialAnalysis/useAnalysisResultSummary";
import { useSaveAnalysisDatasetStatus } from "@/hooks/spatialAnalysis/useSaveAnalysisDatasetStatus";
import useHandleClearView from "@/hooks/ui/useHandleClearView";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { resetAnalysisSummaryState } from "@/store/slice/analysisSummary";
import { setAnalysisId } from "@/store/slice/datasetSlice";
import {
  clearCurrRunningAnalysisId,
  clearLatestAnalysisId,
  setLatestSaveAsDataset
} from "@/store/slice/spatialSlice";
import {
  ESidebarMenuType,
  setAnalysisResultPopup,
  setShowAnalysisSummaryPopup,
  setSideBarMenuTypePopupDisplay,
  toggleAnalysisJobList,
  toggleAnalysisResultPopup,
  toggleDatasetExplorerPopup,
  toggleShowProjectList
} from "@/store/slice/uiSlice";
import { AreaInputType, ECatchmentInputBy } from "@/types/spatial";
import { formatStorageFromKB } from "@/utils/format";

import { twMerge } from "tailwind-merge";

import SpatialHeader from "../header";
import SaveSuccessNotiContent from "../notification/saveConfigurationSuccess";
import SaveAsDatasetPopup, {
  DatasetEntryFormData
} from "../saveAsDatasetPopup";
import SaveConfigurationPopup, {
  ConfigurationFormData
} from "../saveConfigurationPopup";

const TEST_ID_PREFIX = "spatial-analysis-result-popup";

type AnalysisResultPopupProps = {
  onClose: () => void;
};

const AnalysisResultPopup = ({ onClose }: AnalysisResultPopupProps) => {
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const {
    handleResetDatasetToHistoryDataset,
    removeAllCatchmentMarkers,
    renderDrawPolygon,
    resetAllFeatureStates
  } = useMap();
  const { handleCloseDetailView } = useHandleClearView();
  const { fetchSummary } = useAnalysisResultSummary();
  // Editing before the config finishes hydrating would open the panel with
  // partial analysis config data.
  const isAnalysisConfigHydrating = useAnalysisConfigHydration() === "loading";
  const {
    isBVTDataForCatchmentEnabled,
    isDatasetWidgetEnabled,
    isAnalysisConfigViewOnlyEnabled,
    isV2SaveDatasetEnabled
  } = useAppFeatures();

  const {
    isEdit,
    currentConfigId,
    spatialConfig,
    catchmentSettings,
    title,
    description,
    latestAnalysisId,
    latestSaveAsDataset
  } = useSelector((state: RootState) => state.spatial);
  const {
    showAnalysisJobList,
    showAnalysisSummaryPopup,
    isAnalysisConfigMinimized
  } = useSelector((state: RootState) => state.ui);
  const [showSaveConfig, setShowSaveConfig] = useState<boolean>(false);
  const [showSaveDataset, setShowSaveDataset] = useState<boolean>(false);
  const [projectNameExist, setProjectNameExist] = useState(false);

  const { mutateSaveConfig } = useConfiguration({
    onSaveConfigSuccess: () => {
      setShowSaveConfig(false);
    },
    onSaveConfigError: (error) => setProjectNameExist(error.code === 409),
    onOpenProjectFromToast: () => {
      removeAllCatchmentMarkers();
      handleResetDatasetToHistoryDataset();
      dispatch(toggleAnalysisResultPopup());
      dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
      dispatch(toggleShowProjectList(true));
    }
  });

  const handleSaveDatasetError = (error: SaveAnalysisUserDatasetError) => {
    const showError = (message: React.ReactNode, options = {}) =>
      showToast({ type: EToastType.ERROR, ...options, message });

    if (error.message.reason === "ERR_DATASET_ALREADY_EXISTS") {
      return showError(
        <NotificationContent
          title="Dataset Already Saved"
          description={getDisplayErrorMessage(error)}
        />,
        { autoClose: false }
      );
    }

    if (error.message.reason === "ERR_DATASET_SAVE_DUPLICATE_NAME") {
      return showError(
        <NotificationContent
          title="Duplicate Name"
          description={getDisplayErrorMessage(error)}
        />,
        { autoClose: false }
      );
    }

    const availableKB = error.message.details?.find(
      (detail) => detail.field === "available_kb"
    );

    const convertAvailableKB = availableKB?.message
      ? Number.parseInt(availableKB?.message)
      : 0;

    if (availableKB) {
      return showError(
        <NotificationContent>
          <p>
            Save failed. Only {formatStorageFromKB(convertAvailableKB)} quota
            left. Delete old datasets or upgrade your plan.
          </p>
        </NotificationContent>
      );
    }

    showError(getDisplayErrorMessage(error), {
      autoClose: false
    });
  };

  /* Only persisted once the async operation succeeds, so a failed save leaves
     the datasets re-savable under the same names. */
  const { startPolling } = useSaveAnalysisDatasetStatus({
    onSuccess: (savedDatasets) => {
      dispatch(
        setLatestSaveAsDataset({
          datasetsInfo: [
            ...(latestSaveAsDataset?.datasetsInfo ?? []),
            ...savedDatasets
          ]
        })
      );
    },
    onOpenDataExplorer: handleOpenDataExplorer
  });

  const { mutate: mutateSaveAsDatasetV2 } =
    useSaveAnalysisUserDatasetV2Mutation({
      onMutate: () => {
        showToast({
          message: MESSAGE.SAVING_YOUR_DATASET,
          type: EToastType.LOADING
        });
      },
      onError: handleSaveDatasetError
    });

  const { mutate: mutaveSaveAsDataset } = useSaveAnalysisUserDatasetMutation({
    onMutate: () => {
      showToast({
        message: MESSAGE.SAVING_YOUR_DATASET,
        type: EToastType.LOADING
      });
    },
    onSuccess: () => {
      setShowSaveDataset(false);
      showToast({
        type: EToastType.SUCCESS,
        message: (
          <SaveSuccessNotiContent
            title={MESSAGE.ANALYSIS_SAVE_AS_USER_DATASET_SUCCESS}
            description={
              MESSAGE.ANALYSIS_SAVE_AS_USER_DATASET_SUCCESS_DESCRIPTION
            }
            buttonLabel="Open data explorer"
            onOpen={handleOpenDataExplorer}
          />
        ),
        autoClose: false
      });
    },
    onError: handleSaveDatasetError
  });

  const getSaveAsOptionMenu = (): ButtonMenuItem[] => {
    return [
      { name: "Datasets", onClick: () => setShowSaveDataset(true) },
      {
        id: "existing-project",
        name: "Existing project",
        onClick: () => {
          mutateSaveConfig({
            id: currentConfigId,
            name: title ?? "",
            description: description ?? ""
          });
        },
        hide: !isEdit
      },
      {
        name: "New project",
        onClick: () => setShowSaveConfig(true)
      }
    ];
  };

  const handleSaveConfiguration = (data: ConfigurationFormData) => {
    mutateSaveConfig(data);
  };

  const handleSaveDataset = (data: DatasetEntryFormData) => {
    if (!latestAnalysisId) return;

    const payload = {
      analysisId: latestAnalysisId,
      datasets: data.datasetsInfo.map((item) => ({
        id: item.datasetId,
        name: item.datasetName,
        short_desc: item.description
      }))
    };

    if (isV2SaveDatasetEnabled) {
      mutateSaveAsDatasetV2(payload, {
        onSuccess: ({ operation_id }) => {
          setShowSaveDataset(false);
          startPolling(operation_id, data.datasetsInfo);
        }
      });
      return;
    }

    mutaveSaveAsDataset(payload, {
      onSuccess: () => {
        /* Save the latest save as dataset in the store, using for next
            time to save as dataset, Prevent the same dataset results from being saved multiple times */
        // Merge previous and current datasets
        dispatch(
          setLatestSaveAsDataset({
            datasetsInfo: [
              ...(latestSaveAsDataset?.datasetsInfo ?? []),
              ...data.datasetsInfo
            ]
          })
        );
      }
    });
  };

  const handleClickViewSummary = () => {
    resetAllFeatureStates();
    dispatch(setShowAnalysisSummaryPopup(true));
    if (!isDatasetWidgetEnabled) {
      dispatch(setAnalysisResultPopup(false));
      // fetchSummary dispatches setAnalysisSummaryId internally so the Redux
      // ID always matches the in-flight query key.
      fetchSummary(latestAnalysisId as string);
    }
    if (showAnalysisJobList) {
      dispatch(toggleAnalysisJobList());
    }
  };

  const handleClearAIConversation = () => {
    localStorage.removeItem("lokasi_ai_conversations");
  };

  const handleClosePopup = () => {
    if (spatialConfig?.areaInput?.value === AreaInputType.POLYGON) {
      renderDrawPolygon();
    }
    handleCloseDetailView();
    handleClearAIConversation();
    dispatch(clearLatestAnalysisId());
    dispatch(clearCurrRunningAnalysisId());
    dispatch(resetAnalysisSummaryState());
    dispatch(setAnalysisId(null));
    onClose();
  };

  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault();
  }, []);

  const handleClickEdit = () => {
    toast.dismiss();
    resetAllFeatureStates();
    handleCloseDetailView();
    removeAllCatchmentMarkers();
    handleResetDatasetToHistoryDataset();
    if (
      spatialConfig?.areaInput?.value === AreaInputType.POLYGON ||
      (isBVTDataForCatchmentEnabled &&
        spatialConfig?.areaInput?.value === AreaInputType.CATCHMENT &&
        catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_POLYGON)
    ) {
      renderDrawPolygon();
    }
    dispatch(resetAnalysisSummaryState());
    dispatch(setAnalysisId(null));
    dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
    dispatch(toggleAnalysisResultPopup());
    dispatch(setLatestSaveAsDataset(null));
    dispatch(clearLatestAnalysisId());
    dispatch(clearCurrRunningAnalysisId());
    handleClearAIConversation();
    if (isDatasetWidgetEnabled) {
      dispatch(setShowAnalysisSummaryPopup(false));
    }
  };

  function handleOpenDataExplorer() {
    handleResetDatasetToHistoryDataset();
    dispatch(toggleAnalysisResultPopup());
    dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
    dispatch(toggleDatasetExplorerPopup(true));
    handleClearAIConversation();
  }

  useEffect(() => {
    // Close detail view on the map when the popup is opened
    handleCloseDetailView();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={twMerge(
        "absolute z-[9] border-gainsboro bg-white rounded-lg w-[338px]",
        "flex flex-col",
        // Shifted right only when the expanded read-only Analysis Config panel
        // is there to fill the gap; otherwise this panel keeps the map's left
        // margin. Minimized, that panel is a 56px bar at top-[30px], so this
        // one slides under it (30 + 56 + 8px gap).
        isAnalysisConfigViewOnlyEnabled && !isAnalysisConfigMinimized
          ? "left-[370px]"
          : "left-[16px]",
        isAnalysisConfigViewOnlyEnabled && isAnalysisConfigMinimized
          ? "top-[94px] max-h-[calc(90vh-64px)]"
          : "top-[30px] max-h-[90vh]"
      )}
      data-testid="analysis-result-popup"
    >
      <div className="relative overflow-y-auto">
        <SpatialHeader
          title="Analysis Result"
          onClose={handleClosePopup}
          withCloseButton={
            !(isDatasetWidgetEnabled && showAnalysisSummaryPopup)
          }
        />
        <div className="flex flex-col px-4 pb-4">
          <p className="text-[10px]">
            Adjust the color scale, opacity, and save your configuration.
          </p>
          <DatasetList
            isSpatialAnalysis={true}
            onOpenDatasetTabularModal={() => {}}
          />
          {/* Button group */}
          <div className="flex gap-2 mt-3 w-full">
            <Button
              disabled={isDatasetWidgetEnabled && showAnalysisSummaryPopup}
              variant="outline"
              className="w-1/2 text-sm font-bold h-8 rounded-xl"
              onClick={handleClickViewSummary}
            >
              View Summary
            </Button>
            <ButtonMenu
              title="Save as"
              className="w-1/2"
              btnContainerClassName="btn-primary fill-white flex justify-between !px-4 h-8"
              menuClassName="w-[147px] right-0 h-fit z-[99]"
              menu={getSaveAsOptionMenu()}
              data-testid={`${TEST_ID_PREFIX}-save-as-button`}
            />
          </div>
          {/* Export */}
          <div className="flex justify-center text-[10px] cursor-pointer mt-4">
            <span>Need to adjust your input? </span>&nbsp;
            <span>Click</span>&nbsp;
            <Button
              onClick={handleClickEdit}
              disabled={isAnalysisConfigHydrating}
              data-testid={`${TEST_ID_PREFIX}-edit-input-button`}
              variant="link"
              className="text-royal-blue font-bold p-0 h-auto text-[10px] hover:no-underline"
            >
              Edit Input
            </Button>
          </div>
        </div>
      </div>
      {showSaveConfig && (
        <SaveConfigurationPopup
          show
          nameIsExist={projectNameExist}
          setNameIsExist={setProjectNameExist}
          onClose={() => setShowSaveConfig(false)}
          onSave={handleSaveConfiguration}
        />
      )}
      {showSaveDataset && (
        <SaveAsDatasetPopup
          show
          onClose={() => setShowSaveDataset(false)}
          onSave={handleSaveDataset}
        />
      )}
    </div>
  );
};

export default AnalysisResultPopup;
